import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/pages/news/components/NewsCard";
import useSEO from "@/hooks/useSEO";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 6;

const PERIODS = [
  { value: "all", label: "全部时间", days: 0 },
  { value: "week", label: "近一周", days: 7 },
  { value: "month", label: "近一个月", days: 31 },
  { value: "half", label: "近半年", days: 183 },
];

export default function NewsPage() {
  const [period, setPeriod] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const selected = PERIODS.find((item) => item.value === period);
    const now = new Date();
    const cutoff =
      selected && selected.days > 0
        ? new Date(now.getTime() - selected.days * 24 * 60 * 60 * 1000)
        : null;
    const word = keyword.trim().toLowerCase();

    return newsList
      .filter((item) => {
        if (cutoff && new Date(item.date) < cutoff) return false;
        if (word && !`${item.title} ${item.summary}`.toLowerCase().includes(word)) return false;
        return true;
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [period, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const periodLabel = PERIODS.find((item) => item.value === period)?.label ?? "全部时间";

  useSEO({
    title:
      period !== "all" || keyword
        ? `${keyword ? `“${keyword}”相关` : ""}${periodLabel}新闻动态 - 日本女优大全`
        : "新闻动态 - 日本女优作品资讯与人物动态",
    description:
      period !== "all" || keyword
        ? `日本女优新闻动态：当前筛选${periodLabel}${keyword ? `，关键词“${keyword}”` : ""}，共 ${filtered.length} 条资讯，涵盖作品资讯、人物动态与行业观察。`
        : "日本女优新闻动态列表，整理作品资讯、女优人物动态与行业观察，支持按时间段与关键词筛选，按发布时间倒序展示。",
    keywords: "日本女优新闻,女优动态,作品资讯,日本影视新闻",
  });

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "新闻动态" }]} />

      <div className="mb-6 border-b border-background-200 pb-5">
        <p className="font-label text-[11px] tracking-[0.2em] text-primary-600">NEWS & UPDATES</p>
        <h1 className="mt-1.5 font-heading text-[26px] font-black leading-tight text-foreground-950 md:text-[32px]">
          新闻动态
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          汇总作品资讯、女优人物动态与行业观察。可按时间段筛选，或输入关键词检索标题与摘要，点击条目阅读全文。
        </p>
      </div>

      <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 flex items-center gap-1.5 font-label text-sm font-medium text-foreground-900">
              <i className="ri-calendar-line text-primary-600" aria-hidden="true"></i>
              发布时间
            </p>
            <div className="flex flex-wrap gap-2">
              {PERIODS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    setPeriod(item.value);
                    setPage(1);
                  }}
                  aria-pressed={period === item.value}
                  className={`cursor-pointer rounded-md border px-3 py-1.5 font-label text-xs transition-colors ${
                    period === item.value
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full md:w-[300px]">
            <label
              htmlFor="news-keyword"
              className="mb-2 flex items-center gap-1.5 font-label text-sm font-medium text-foreground-900"
            >
              <i className="ri-search-line text-primary-600" aria-hidden="true"></i>
              关键词搜索
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
              <input
                id="news-keyword"
                type="text"
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setPage(1);
                }}
                placeholder="输入标题或摘要关键词"
                className="w-full bg-transparent font-label text-xs text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword ? (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => {
                    setKeyword("");
                    setPage(1);
                  }}
                  className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-primary-600"
                >
                  <i className="ri-close-line" aria-hidden="true"></i>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="font-label text-xs text-foreground-500">
          共 <span className="font-medium text-primary-700">{filtered.length}</span> 条资讯 · {periodLabel}
          {keyword ? ` · 关键词“${keyword}”` : ""}
        </p>
        {(period !== "all" || keyword) && (
          <button
            type="button"
            onClick={() => {
              setPeriod("all");
              setKeyword("");
              setPage(1);
            }}
            className="cursor-pointer font-label text-[11px] text-foreground-500 underline underline-offset-2 hover:text-primary-700"
          >
            清除全部筛选
          </button>
        )}
      </div>

      {visible.length > 0 ? (
        <>
          <div className="space-y-3">
            {visible.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          <p className="mt-4 text-center font-label text-[11px] text-foreground-400">
            每页 6 条 · 当前第 {currentPage} / {totalPages} 页
          </p>
        </>
      ) : (
        <EmptyState
          icon="ri-newspaper-line"
          title="没有符合条件的新闻"
          description="当前时间段与关键词组合下没有相关资讯。可以扩大时间范围，或换一个关键词再试。"
          onReset={() => {
            setPeriod("all");
            setKeyword("");
            setPage(1);
          }}
          actionLabel="返回首页"
          actionHref="/"
        />
      )}
    </SiteLayout>
  );
}